import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  useGetFieldAdminHSToolboxQuery,
  useSaveFieldAdminHSToolboxTalkMutation,
  useAcknowledgeFieldAdminHSToolboxMutation,
} from "../fieldAdminApi";
import { CARD_BG, BORDER } from "../../../Global/colors";
import { FONT_FAMILY, installerLabel, formatTimeOfDay } from "../designTokens";
import { HSPageShell, HSSectionLabel, HSNotesBlock, HSFooterActions } from "./HSStepShell";
import { FAErrorCard, FASkeletonRows } from "../FAStates";
import { InstallerAvatar, mergeRosterRows, useTeamRoster } from "./InstallerRoster";
import { FASelect } from "../StockFormShell";
import { FACheckbox, useConfirm } from "../FAControls";
import { FAEmptyState } from "../FALayout";

const TOPICS = [
  "Lockout-tagout procedures for live distribution boards",
  "Working at height — ladder safety & fall arrest",
  "PPE: insulated gloves & arc-flash protection",
  "Heat stress & hydration during summer rollouts",
  "Manual handling — geyser removal & replacement",
  "Vehicle safety & load securement",
  "Site-specific risks · ad-hoc",
];

// acknowledged_at is a UTC instant now, not a bare TimeField string, so it uses the same
// formatTimeOfDay every other screen does and renders in the viewer's own zone. The
// optimistic tick below writes a real ISO string so both paths agree on the shape.

export default function Toolbox() {
  const navigate = useNavigate();
  const { data, isSuccess, isError, refetch } = useGetFieldAdminHSToolboxQuery();
  const [saveTalk, { isLoading: isSaving }] = useSaveFieldAdminHSToolboxTalkMutation();
  const [acknowledge] = useAcknowledgeFieldAdminHSToolboxMutation();

  const [topic, setTopic] = useState(TOPICS[0]);
  const [body, setBody] = useState("");
  const [notes, setNotes] = useState("");
  const { installers, nameById, isRosterReady, isRosterError, refetchRoster } = useTeamRoster();
  const [ackRows, setAckRows] = useState(null);
  const { ask, confirmDialog } = useConfirm();

  // Everyone briefed needs a row to tick, so the list is the team roster rather than
  // whoever happens to have acknowledged already — that also makes the x / y denominator
  // the real headcount instead of growing as people tick.
  useEffect(() => {
    if (!isSuccess || !isRosterReady || ackRows !== null) return;
    setTopic(data.topic || TOPICS[0]);
    setBody(data.body || "");
    setNotes(data.notes || "");
    setAckRows(mergeRosterRows(installers, data.acknowledgements, (ack, installerId) => ({
      installer_id: installerId,
      acknowledged: ack?.acknowledged ?? false,
      acknowledged_at: ack?.acknowledged_at ?? null,
    })));
  }, [isSuccess, data, ackRows, installers, isRosterReady]);

  if (ackRows === null) {
    return (
      <HSPageShell step={{ stepNumber: 4, title: "Toolbox Talk", countLabel: "" }}>
        {/* Blocked on the roster as well as this step's own data — see Tools. */}
        {isError || isRosterError
          ? <FAErrorCard onRetry={isRosterError ? refetchRoster : refetch} />
          : <FASkeletonRows count={5} />}
      </HSPageShell>
    );
  }

  const ackDone = ackRows.filter((r) => r.acknowledged).length;

  const handleAck = async (installerId) => {
    const result = await acknowledge({ installer_id: installerId, acknowledged: true });
    if (!result.error) {
      setAckRows((prev) => prev.map((r) => (r.installer_id === installerId ? { ...r, acknowledged: true, acknowledged_at: new Date().toISOString() } : r)));
    }
  };

  // See Attendance.saveAll — returning false keeps HSFooterActions on the page.
  const saveTalkDetails = async () => {
    const result = await saveTalk({ topic, body, notes });
    return !result.error;
  };

  const handleMarkComplete = async () => {
    if (ackRows.length === 0 || ackDone < ackRows.length) {
      const proceed = await ask({
        title: "Complete with acknowledgements pending?",
        body: ackRows.length === 0
          ? "No installers on this team\u2019s roster have acknowledged today\u2019s toolbox talk. Mark it complete anyway?"
          : `${ackRows.length - ackDone} of ${ackRows.length} installers have not acknowledged today\u2019s toolbox talk. Mark it complete anyway?`,
        confirmLabel: "Mark complete",
        cancelLabel: "Keep briefing",
        danger: true,
      });
      if (!proceed) return;
    }
    if (!(await saveTalkDetails())) return;
    navigate("/field-admin/hs/devices/");
  };

  return (
    <HSPageShell step={{ stepNumber: 4, title: "Toolbox Talk", countLabel: `${ackDone} / ${ackRows.length} acknowledged` }}>

      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "16px", mb: "22px" }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#fff", mb: "6px" }}>
          Today's topic <Box component="span" sx={{ color: "#F87171" }}>*</Box>
        </Typography>
        <FASelect fullWidth value={topic} onChange={(e) => setTopic(e.target.value)} size="small">
          {TOPICS.map((t) => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </FASelect>

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#fff", mt: "18px", mb: "6px" }}>
          Topic content / talking points
        </Typography>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write out the key points you covered — hazards, control measures, and any reminders for the team."
          rows={6}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#0d1117", color: "#fff", padding: "12px 14px", fontFamily: FONT_FAMILY, fontSize: "0.85rem" }}
        />
      </Box>

      <HSSectionLabel right="Each installer ticks once briefed · timestamp logged">Acknowledgement</HSSectionLabel>

      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
        {ackRows.length === 0 ? (
          <FAEmptyState py="30px">No installers on this team's roster.</FAEmptyState>
        ) : (
          ackRows.map((r, index) => (
            <Box
              key={r.installer_id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                px: "16px",
                py: "12px",
                borderBottom: index === ackRows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)",
                backgroundColor: r.acknowledged ? "rgba(34,197,94,0.04)" : "transparent",
              }}
            >
              <InstallerAvatar name={nameById.get(r.installer_id)} id={r.installer_id} />
              <Typography sx={{ flexGrow: 1, color: "#fff", fontWeight: 600, fontSize: "0.88rem" }}>
                {installerLabel(nameById.get(r.installer_id), r.installer_id)}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: r.acknowledged ? "#4ADE80" : "rgba(255,255,255,0.4)", fontVariantNumeric: "tabular-nums" }}>
                {formatTimeOfDay(r.acknowledged_at) ?? "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>Acknowledged</Typography>
              <FACheckbox
                checked={r.acknowledged}
                disabled={r.acknowledged}
                onChange={() => handleAck(r.installer_id)}
              />
            </Box>
          ))
        )}
      </Box>

      <HSNotesBlock value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Sipho asked for a refresher on tag-out wording — book a safety-officer ride-along." />

      <HSFooterActions onSaveExit={saveTalkDetails} onMarkComplete={handleMarkComplete} markCompleteDisabled={isSaving} />
      {confirmDialog}
    </HSPageShell>
  );
}
